// Entry detail panel. Reveal/hide password with auto-hide, copy with auto-clear.

import { el, clear } from './dom.js';
import { copyWithAutoClear } from './clipboard.js';
import { avatarFor } from '../util/avatar.js';
import { fetchFavicon } from '../util/favicon.js';
import { openMenu } from './menu.js';
import { icon } from './icons.js';
import { parseOtpInput, generateTotp } from '../util/totp.js';
import { formatBytes } from '../util/format-bytes.js';
import { safeHttpUrl } from '../util/safe-url.js';

const REVEAL_MS = 10_000;

// Track object URLs created in the current render so we can revoke them on
// the next render. Prevents leaks as the user clicks between entries.
let activeObjectURLs = [];

function revokeAll() {
  for (const u of activeObjectURLs) URL.revokeObjectURL(u);
  activeObjectURLs = [];
}

// Track live TOTP timers so they're cleared when navigating away or locking,
// the same way activeObjectURLs are revoked.
let activeTimers = [];

function clearTimers() {
  for (const id of activeTimers) clearInterval(id);
  activeTimers = [];
}

// Tear down everything the current render owns (live TOTP timers + attachment
// object URLs). Call this when the detail pane goes away without a re-render —
// notably on lock — so no interval keeps firing and no decrypted TOTP secret is
// retained in a timer closure after lock().
export function teardownDetail() {
  revokeAll();
  clearTimers();
}

export function renderDetail(container, { entry, vault, onEdit, onToggleStar, onDelete, onBack, onMutated }) {
  revokeAll();
  clearTimers();
  clear(container);
  if (!entry) {
    container.append(el('div', { class: 'empty-detail' }, ['Select an entry to view.']));
    return;
  }

  const header = el('div', { class: 'detail-header' }, [
    detailIcon(entry, vault, onMutated),
    el('div', { class: 'detail-header-text' }, [
      el('div', { class: 'type' }, [labelFor(entry.type)]),
      el('h2', {}, [entry.title || '(untitled)']),
    ]),
    el('div', { class: 'actions' }, [
      starButton(entry, onToggleStar),
      el('button', { onclick: onEdit, title: 'Edit (⌘E)' }, ['Edit']),
      el('button', { class: 'danger', onclick: () => {
        if (confirm(`Delete “${entry.title || 'this entry'}”?`)) onDelete();
      } }, ['Delete']),
    ]),
  ]);

  const body = el('div', { class: 'detail-body' });
  for (const f of fieldsFor(entry)) body.append(fieldRow(f));
  if (entry.type === 'login' && entry.totp) body.append(totpRow(entry.totp));
  if (entry.type === 'login' && entry.backupCodes) body.append(backupCodesBlock(entry.backupCodes));
  if (entry.type === 'note' && entry.body) body.append(noteBlock('Body', entry.body));
  if (entry.notes) body.append(noteBlock('Notes', entry.notes));

  if (Array.isArray(entry.attachments) && entry.attachments.length && vault) {
    body.append(attachmentsBlock(entry.attachments, vault));
  }

  container.append(header, body);
}

function starButton(entry, onToggleStar) {
  const starred = !!entry.starred;
  return el('button', {
    class: 'star-btn' + (starred ? ' starred' : ''),
    title: starred ? 'Unstar' : 'Star',
    'aria-label': starred ? 'Unstar' : 'Star',
    'aria-pressed': starred ? 'true' : 'false',
    onclick: () => onToggleStar?.(),
  }, [icon(starred ? 'star' : 'star_outline', { size: 20 })]);
}

function labelFor(type) {
  return ({ login: 'Login', card: 'Card', license: 'License', identity: 'Identity', note: 'Note', apiKey: 'API Key' })[type] ?? type;
}

function fieldsFor(e) {
  switch (e.type) {
    case 'login': return [
      { label: 'Username', value: e.username, copyable: true },
      { label: 'Password', value: e.password, copyable: true, secret: true, mono: true },
      e.url ? { label: 'URL', value: e.url, link: true } : null,
      ...(Array.isArray(e.customFields) ? e.customFields.map((cf) => ({ label: cf.name || 'Field', value: cf.value, copyable: true })) : []),
    ].filter(Boolean);
    case 'card': return [
      { label: 'Cardholder', value: e.cardholder, copyable: true },
      { label: 'Number', value: e.number, copyable: true, secret: true, mono: true },
      { label: 'Expiry', value: [e.expiryMonth, e.expiryYear].filter(Boolean).join(' / ') },
      { label: 'CVV', value: e.cvv, copyable: true, secret: true, mono: true },
      e.pin ? { label: 'PIN', value: e.pin, copyable: true, secret: true, mono: true } : null,
    ].filter(Boolean);
    case 'license': return [
      e.product ? { label: 'Product', value: e.product } : null,
      e.version ? { label: 'Version', value: e.version } : null,
      { label: 'License key', value: e.licenseKey, copyable: true, secret: true, mono: true },
      e.email ? { label: 'Email', value: e.email, copyable: true } : null,
      e.orderNumber ? { label: 'Order #', value: e.orderNumber, copyable: true } : null,
      e.purchaseDate ? { label: 'Purchased', value: e.purchaseDate } : null,
    ].filter(Boolean);
    case 'identity': return [
      { label: 'Name', value: [e.firstName, e.lastName].filter(Boolean).join(' ') },
      e.email ? { label: 'Email', value: e.email, copyable: true } : null,
      e.phone ? { label: 'Phone', value: e.phone, copyable: true } : null,
      e.address ? { label: 'Address', value: e.address, copyable: true } : null,
    ].filter(Boolean);
    case 'apiKey': return [
      e.service ? { label: 'Service', value: e.service } : null,
      { label: 'Key', value: e.key, copyable: true, secret: true, mono: true },
      e.secret ? { label: 'Secret', value: e.secret, copyable: true, secret: true, mono: true } : null,
    ].filter(Boolean);
    case 'note': return [];
    default: return [];
  }
}

function fieldRow(f) {
  const valueNode = el('span', { class: 'value' + (f.mono ? ' mono' : '') });
  let revealed = !f.secret;
  let hideTimer = null;

  const updateValue = () => {
    if (f.link) {
      valueNode.innerHTML = '';
      const href = safeHttpUrl(f.value);
      // Only linkify schemes that are inert to click. Anything else (a
      // javascript: URL from an untrusted 1PIF/CSV import, say) renders as
      // plain text so it can be read but never navigated to.
      valueNode.append(href
        ? el('a', { href, target: '_blank', rel: 'noopener noreferrer' }, [f.value])
        : document.createTextNode(f.value ?? ''));
      return;
    }
    valueNode.textContent = revealed ? (f.value ?? '') : '•'.repeat(Math.min(12, (f.value ?? '').length || 8));
  };

  const actions = el('div', { class: 'actions' });
  if (f.secret) {
    const revealBtn = el('button', { class: 'icon ghost', onclick: () => {
      revealed = !revealed;
      updateValue();
      revealBtn.textContent = revealed ? 'Hide' : 'Show';
      if (hideTimer) clearTimeout(hideTimer);
      if (revealed) {
        hideTimer = setTimeout(() => {
          revealed = false; updateValue(); revealBtn.textContent = 'Show';
        }, REVEAL_MS);
        // Register so teardownDetail() can clear it. Otherwise revealing a
        // password and locking within REVEAL_MS leaves a pending callback
        // holding the cleartext in its closure, firing at a detached node —
        // the same leak backupCodesBlock already guards against.
        activeTimers.push(hideTimer);
      }
    } }, ['Show']);
    actions.append(revealBtn);
  }
  if (f.copyable && f.value) {
    actions.append(el('button', { class: 'icon ghost', onclick: () => copyWithAutoClear(f.value, `${f.label} copied`) }, ['Copy']));
  }

  updateValue();
  return el('div', { class: 'field-row' }, [
    el('span', { class: 'label' }, [f.label]),
    valueNode,
    actions,
  ]);
}

function formatCode(code) {
  if (code.length === 6) return `${code.slice(0, 3)} ${code.slice(3)}`;
  if (code.length === 8) return `${code.slice(0, 4)} ${code.slice(4)}`;
  return code;
}

function totpRow(rawInput) {
  const parsed = parseOtpInput(rawInput);
  const valueNode = el('span', { class: 'value mono' });
  const actions = el('div', { class: 'actions' });
  const row = el('div', { class: 'field-row' }, [
    el('span', { class: 'label' }, ['One-time password']),
    valueNode,
    actions,
  ]);

  if (!parsed) {
    valueNode.classList.remove('mono');
    valueNode.classList.add('muted');
    valueNode.textContent = 'Invalid 2FA secret';
    return row;
  }

  let currentCode = '';
  let lastCounter = -1;
  const countdown = el('span', { class: 'totp-countdown' });
  const copyBtn = el('button', { class: 'icon ghost', onclick: () => {
    if (currentCode) copyWithAutoClear(currentCode, 'Code copied');
  } }, ['Copy']);
  actions.append(countdown, copyBtn);

  async function tick() {
    const now = Math.floor(Date.now() / 1000);
    countdown.textContent = `${parsed.period - (now % parsed.period)}s`;
    const counter = Math.floor(now / parsed.period);
    if (counter === lastCounter) return;
    lastCounter = counter;
    try {
      currentCode = await generateTotp(parsed, now);
      valueNode.textContent = formatCode(currentCode);
    } catch {
      currentCode = '';
      valueNode.textContent = '—';
    }
  }

  tick();
  activeTimers.push(setInterval(tick, 1000));
  return row;
}

function noteBlock(label, body) {
  return el('div', {}, [
    el('div', { class: 'label', style: { fontSize: '11px', color: 'var(--muted-2)', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 4px 6px' } }, [label]),
    el('div', { class: 'note-block' }, [body]),
  ]);
}

function backupCodesBlock(codes) {
  const valueNode = el('div', { class: 'note-block mono backup-codes-value' });
  let revealed = false;
  let hideTimer = null;

  const update = () => { valueNode.textContent = revealed ? codes : '••••••••'; };

  const revealBtn = el('button', { class: 'icon ghost', onclick: () => {
    revealed = !revealed;
    update();
    revealBtn.textContent = revealed ? 'Hide' : 'Show';
    if (hideTimer) clearTimeout(hideTimer);
    if (revealed) {
      hideTimer = setTimeout(() => {
        revealed = false; update(); revealBtn.textContent = 'Show';
      }, REVEAL_MS);
      // Registered so teardownDetail()/lock clears it — no codes retained in a closure.
      activeTimers.push(hideTimer);
    }
  } }, ['Show']);

  const copyBtn = el('button', { class: 'icon ghost', onclick: () => copyWithAutoClear(codes, 'Recovery codes copied') }, ['Copy']);

  update();
  return el('div', { class: 'backup-codes' }, [
    el('div', { class: 'label', style: { fontSize: '11px', color: 'var(--muted-2)', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 4px 6px' } }, ['Recovery codes']),
    el('div', { class: 'backup-codes-row' }, [
      valueNode,
      el('div', { class: 'actions' }, [revealBtn, copyBtn]),
    ]),
  ]);
}

function attachmentsBlock(attachments, vault) {
  const grid = el('div', { class: 'attachments-grid' });
  for (const a of attachments) grid.append(attachmentTile(a, vault));
  return el('div', {}, [
    el('div', { class: 'label', style: { fontSize: '11px', color: 'var(--muted-2)', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 4px 6px' } }, [`Attachments (${attachments.length})`]),
    grid,
  ]);
}

function attachmentTile(att, vault) {
  const isImage = /^image\//.test(att.type) && att.type !== 'image/heic';
  const tile = el('div', { class: 'attachment-tile' });
  const thumb = el('div', { class: 'attachment-thumb' }, ['Loading…']);
  const meta = el('div', { class: 'attachment-meta' }, [
    el('div', { class: 'name', title: att.name }, [att.name]),
    el('div', { class: 'size' }, [formatBytes(att.size)]),
  ]);
  tile.append(thumb, meta);

  vault.getAttachmentBytes(att.id).then((bytes) => {
    const blob = new Blob([bytes], { type: att.type || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    activeObjectURLs.push(url);
    thumb.textContent = '';
    // A blob: document inherits the creating page's origin, so opening an
    // attachment as a top-level document runs it in the vault's origin.
    // att.type is caller-influenced (1PIF import derives it from the filename),
    // so an attached .html or .svg could script itself into this origin.
    // Only types the browser renders inertly may be opened; the rest download.
    // Rendering inside <img> stays allowed for all images — an <img> never
    // executes script, not even for SVG.
    const canOpen = openableInline(att.type);
    const downloadLink = el('a', { href: url, download: att.name, class: 'attachment-download' }, ['Download']);
    if (isImage) {
      const img = el('img', { src: url, alt: att.name });
      img.addEventListener('click', () => (canOpen ? window.open(url, '_blank', 'noopener') : downloadLink.click()));
      thumb.append(img);
    } else {
      const verb = canOpen ? 'Open' : 'Download';
      const badge = el('div', { class: 'file-icon', title: `${verb} ${att.name}`, style: { cursor: 'pointer' } }, [extLabel(att.name) || 'FILE']);
      badge.addEventListener('click', () => (canOpen ? window.open(url, '_blank', 'noopener') : downloadLink.click()));
      thumb.append(badge);
    }
    meta.append(downloadLink);
  }).catch((err) => {
    // A DecryptionError means the bytes are present but unreadable. Anything
    // else (e.g. the row isn't here yet) means the file hasn't synced down.
    const pending = err?.name !== 'DecryptionError';
    thumb.textContent = pending ? 'Syncing…' : 'Failed';
    thumb.title = err?.message ?? String(err);
  });

  return tile;
}

function buildIconEl(entry) {
  if (typeof entry.iconDataUrl === 'string' && entry.iconDataUrl.length > 0) {
    return el('img', {
      class: 'detail-icon',
      src: entry.iconDataUrl,
      alt: '',
      width: 40,
      height: 40,
    });
  }
  const { initial, hue } = avatarFor(entry.title || '');
  return el('span', {
    class: 'detail-icon avatar',
    style: { background: `hsl(${hue} 60% 45%)` },
  }, [initial]);
}

function detailIcon(entry, vault, onMutated) {
  const iconEl = buildIconEl(entry);
  if (entry.type !== 'login' || !entry.url || !vault) return iconEl;

  const msg = el('span', { class: 'detail-icon-msg' });

  async function refresh() {
    msg.textContent = 'Fetching…';
    try {
      const dataUrl = await fetchFavicon(entry.url);
      if (dataUrl) {
        const fresh = await vault.get(entry.id);
        if (fresh) {
          await vault.upsert({ ...fresh, iconDataUrl: dataUrl });
          if (onMutated) await onMutated();
          return;
        }
      }
    } catch { /* fetchFavicon never throws, but stay silent regardless */ }
    msg.textContent = "Couldn't fetch icon.";
    setTimeout(() => { msg.textContent = ''; }, 3000);
  }

  const openIconMenu = (e) => {
    e.preventDefault();
    openMenu(iconEl, [{ label: 'Refresh icon', onClick: refresh }]);
  };
  iconEl.classList.add('clickable');
  iconEl.title = 'Icon options';
  iconEl.addEventListener('click', openIconMenu);
  iconEl.addEventListener('contextmenu', openIconMenu);

  return el('div', { class: 'detail-icon-cell' }, [iconEl, msg]);
}

// MIME types safe to hand to window.open from a blob: URL. Anything markup- or
// script-bearing (text/html, image/svg+xml, application/xhtml+xml, …) is
// excluded because such a document would execute in the vault's own origin.
// PDFs render in the browser's sandboxed viewer, not as a same-origin document.
function openableInline(type) {
  const t = (type ?? '').toLowerCase().split(';')[0].trim();
  if (t === 'application/pdf' || t === 'text/plain') return true;
  return t.startsWith('image/') && t !== 'image/svg+xml';
}

function extLabel(name) {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toUpperCase().slice(0, 5) : '';
}
